import React, { useState, useEffect } from 'react';
import { Loader2, Eye, Send, X, UtensilsCrossed, History, Star, Globe2, Inbox } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminRecipesPanel = ({ csrfToken, onLogout }) => {
  const [previewHtml, setPreviewHtml] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [history, setHistory] = useState(null);
  const [gallery, setGallery] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const fetchWithCsrf = (url, opts = {}) => fetch(url, {
    ...opts, credentials: 'include',
    headers: { ...(opts.headers || {}), 'X-CSRF-Token': csrfToken },
  });

  const loadHistory = async () => {
    try {
      const res = await fetchWithCsrf(`${BACKEND_URL}/api/admin/recipes-newsletter-history`);
      if (res.status === 401) { onLogout(); return; }
      if (!res.ok) throw new Error();
      const data = await res.json();
      setHistory(data.history || []);
    } catch {
      setHistory([]);
    }
  };

  const loadGallery = async () => {
    try {
      const res = await fetchWithCsrf(`${BACKEND_URL}/api/admin/community-gallery`);
      if (res.status === 401) { onLogout(); return; }
      if (!res.ok) throw new Error();
      const data = await res.json();
      setGallery(data.recipes || []);
    } catch {
      setGallery([]);
    }
  };

  const toggleFeature = async (recipeId) => {
    setTogglingId(recipeId);
    try {
      const res = await fetchWithCsrf(`${BACKEND_URL}/api/admin/community-gallery/${recipeId}/feature`, { method: 'POST' });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setGallery((prev) => (prev || []).map((r) => (r.id === recipeId ? { ...r, featured: data.featured } : r))
        .sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0)));
    } catch { /* ignore */ }
    finally { setTogglingId(null); }
  };

  useEffect(() => { loadHistory(); loadGallery(); }, []); // eslint-disable-line

  const openPreview = async () => {
    setLoadingPreview(true);
    try {
      const res = await fetchWithCsrf(`${BACKEND_URL}/api/admin/recipes-newsletter-preview`);
      if (res.status === 401) { onLogout(); return; }
      if (!res.ok) throw new Error();
      setPreviewHtml(await res.text());
    } catch {
      setPreviewHtml('<p style="color:#f87171;font-family:sans-serif;padding:20px;">Erreur lors du chargement de l\'aperçu.</p>');
    } finally {
      setLoadingPreview(false);
    }
  };

  const sendNow = async () => {
    if (!window.confirm('Envoyer la newsletter recettes (thème de la semaine) MAINTENANT à tous les abonnés Suivi Pro actifs ?')) return;
    setSending(true);
    setLastResult(null);
    try {
      const res = await fetchWithCsrf(`${BACKEND_URL}/api/admin/run-recipes-newsletter?force=true`, { method: 'POST' });
      if (res.status === 401) { onLogout(); return; }
      const data = await res.json();
      setLastResult(data);
      loadHistory();
    } catch {
      setLastResult({ error: true });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-recipes-panel">
      <div className="bg-zinc-900 border border-white/10 rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <UtensilsCrossed className="w-5 h-5 text-primary" />
          <h3 className="text-white font-bold">Newsletter recettes hebdomadaire</h3>
        </div>
        <p className="text-sm text-zinc-400 mb-4">
          Envoyée automatiquement chaque lundi matin aux abonnés Suivi Pro actifs (thème de la semaine + suggestion du jour).
          Prévisualisez le rendu avant tout envoi manuel.
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={openPreview}
            disabled={loadingPreview}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold bg-zinc-800 text-zinc-200 border border-white/10 hover:border-primary/40 disabled:opacity-50"
            data-testid="recipes-newsletter-preview-btn"
          >
            {loadingPreview ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
            Aperçu
          </button>
          <button
            onClick={sendNow}
            disabled={sending}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold bg-primary text-white hover:bg-primary/90 disabled:opacity-50"
            data-testid="recipes-newsletter-send-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Envoyer maintenant
          </button>
        </div>
        {lastResult && (
          <p className="text-xs mt-3" data-testid="recipes-newsletter-send-result">
            {lastResult.error
              ? <span className="text-red-400">Échec de l'envoi.</span>
              : <span className="text-emerald-400">{lastResult.sent ?? 0}/{lastResult.active ?? 0} abonné(s) notifié(s).</span>}
          </p>
        )}
      </div>

      <div className="bg-zinc-900 border border-white/10 rounded-2xl p-5" data-testid="recipes-newsletter-history">
        <div className="flex items-center gap-2 mb-3">
          <History className="w-5 h-5 text-zinc-400" />
          <h3 className="text-white font-bold">Historique des envois</h3>
        </div>
        {history === null ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 text-zinc-500 animate-spin" /></div>
        ) : history.length === 0 ? (
          <p className="text-sm text-zinc-500" data-testid="recipes-newsletter-history-empty">Aucun envoi enregistré pour le moment.</p>
        ) : (
          <ul className="space-y-2 max-h-80 overflow-y-auto">
            {history.map((h, i) => (
              <li key={i} className="flex items-center justify-between text-sm bg-zinc-800/60 rounded-lg px-3 py-2" data-testid={`recipes-newsletter-history-row-${i}`}>
                <span className="text-zinc-300">
                  {h.type === 'new_recipes' ? `Nouvelles recettes (${h.count ?? '?'})` : `Thème de la semaine${h.forced ? ' (manuel)' : ''}`}
                </span>
                <span className="text-zinc-500 text-xs">{new Date(h.at).toLocaleString('fr-CH', { dateStyle: 'short', timeStyle: 'short' })}</span>
                <span className="text-emerald-400 font-semibold text-xs">{h.sent}/{h.active}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {previewHtml !== null && (
        <div className="fixed inset-0 z-[300] bg-black/80 flex items-center justify-center p-4" onClick={() => setPreviewHtml(null)} data-testid="recipes-newsletter-preview-modal">
          <div className="bg-zinc-900 border border-white/10 rounded-2xl w-full max-w-2xl h-[80vh] max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <p className="text-sm font-bold text-white">Aperçu — newsletter recettes</p>
              <button onClick={() => setPreviewHtml(null)} className="text-zinc-400 hover:text-white" data-testid="recipes-newsletter-preview-close"><X className="w-5 h-5" /></button>
            </div>
            <iframe title="preview" srcDoc={previewHtml} className="flex-1 min-h-0 w-full bg-white" data-testid="recipes-newsletter-preview-iframe" />
          </div>
        </div>
      )}

      <div className="bg-zinc-900 border border-white/10 rounded-2xl p-5" data-testid="admin-community-gallery-panel">
        <div className="flex items-center gap-2 mb-3">
          <Globe2 className="w-5 h-5 text-primary" />
          <h3 className="text-white font-bold">Galerie communauté — Coup de cœur du coach</h3>
        </div>
        <p className="text-sm text-zinc-400 mb-4">
          Épinglez une recette partagée par un client pour la mettre en avant en tête de la
          <a href="/recettes-communaute" target="_blank" rel="noreferrer" className="text-primary hover:underline"> galerie publique</a> avec un badge "Coup de cœur du coach".
        </p>
        {gallery === null && <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 text-zinc-500 animate-spin" /></div>}
        {gallery && gallery.length === 0 && (
          <div className="text-center py-8" data-testid="admin-community-gallery-empty">
            <Inbox className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
            <p className="text-sm text-zinc-500">Aucune recette partagée par la communauté pour l'instant.</p>
          </div>
        )}
        {gallery && gallery.length > 0 && (
          <div className="grid sm:grid-cols-2 gap-3">
            {gallery.map((r) => (
              <div key={r.id} className={`rounded-xl border p-3 flex items-center gap-3 transition-colors ${r.featured ? 'border-yellow-500/50 bg-yellow-500/5' : 'border-white/10 bg-zinc-800/40'}`} data-testid={`admin-gallery-recipe-${r.id}`}>
                {r.image_url ? <img src={r.image_url} alt="" className="w-14 h-14 rounded-lg object-cover shrink-0" /> : <div className="w-14 h-14 rounded-lg bg-zinc-700 flex items-center justify-center shrink-0"><UtensilsCrossed className="w-5 h-5 text-zinc-400" /></div>}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-white truncate">{r.name}</p>
                  <p className="text-xs text-zinc-500">{Math.round(r.macros?.calories || 0)} kcal · {r.views || 0} vues</p>
                </div>
                <button onClick={() => toggleFeature(r.id)} disabled={togglingId === r.id}
                  className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center border transition-colors disabled:opacity-60 ${r.featured ? 'bg-yellow-500/20 border-yellow-500/50 text-yellow-300' : 'border-white/10 text-zinc-400 hover:border-yellow-500/40 hover:text-yellow-200'}`}
                  data-testid={`admin-gallery-feature-btn-${r.id}`} title="Coup de cœur du coach">
                  {togglingId === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Star className={`w-4 h-4 ${r.featured ? 'fill-yellow-300' : ''}`} />}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
